import { Dispatch, SetStateAction } from 'react';
import clsx from 'clsx';
import { OrderLine } from '@interfaces/models/orderLine';
import { NegotiationsUtils } from '@services/negotiations-utils';
import { useNegotiation } from '@context/negotiation.context';
import { CartContext } from '@context/cart.context';
import { useContextSelector } from 'use-context-selector';
import logger from '@helpers/utils/logger/client';
import {
  AllowedNegotiationActions,
  AllowedNegotiationModalActions,
  Negotiation,
} from '@interfaces/models/negotiation/negotiation';
import { Product } from '@interfaces/models/product';
import { useGetNegotiationStatus } from '@hooks/negotiations/useNegotiationStatus';
import { mapNegotiationToNegotiationOfferStatus } from '@interfaces/models/negotiation/mappers/negotiationOfferStatusMapper';
import { StatusMessage } from './status-message/status-message';
import { StatusAction } from './status-action/status-action';
import styles from './status-component.module.scss';

interface StatusComponentProps {
  nextActions: AllowedNegotiationActions[];
  negotiation: Negotiation;
  isLastOffer: boolean;
  negotiationProduct: Product;
  onClose: () => void;
  setErrorMessage: Dispatch<SetStateAction<string>>;
  isSeller: boolean;
  selectAction: (action: AllowedNegotiationModalActions) => void;
  setLoading: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  isNextActionsShown: boolean;
  setIsNextActionsShown: Dispatch<SetStateAction<boolean>>;
}

export const StatusComponent = ({
  nextActions,
  negotiation,
  isLastOffer,
  negotiationProduct,
  onClose,
  setErrorMessage,
  isSeller,
  selectAction,
  setLoading,
  isLoading,
  isNextActionsShown,
  setIsNextActionsShown,
}: StatusComponentProps) => {
  const { setNegotiation } = useNegotiation();
  const addToShoppingCart = useContextSelector(CartContext, (v) => v.addToShoppingCart);
  const productsInCart = useContextSelector(CartContext, (v) => v.productsInCart);
  const isProductIdInCart = useContextSelector(CartContext, (v) => v.isProductIdInCart);
  const isOrderButtonDisabled =
    isLoading || !negotiation.isAccepted || isProductIdInCart(productsInCart, negotiationProduct.id);
  const isSingleBtn = nextActions?.length === 1;

  const negotiationFormatted = mapNegotiationToNegotiationOfferStatus(negotiation, false);
  const offerStatus = useGetNegotiationStatus(negotiationFormatted, isSeller, isLastOffer);

  const lastEventOfferDetails = negotiation.events?.[0]?.offerDetails;

  const addToCart = async (): Promise<void> => {
    setLoading(true);
    try {
      const orderLine: OrderLine = await addToShoppingCart(negotiationProduct, false);
      if (orderLine) {
        onClose?.();
      }
    } catch (e) {
      logger.error(e, 'Error adding to cart - Negotiation-modal');
    } finally {
      setLoading(false);
    }
  };

  const acceptOffer = async (): Promise<void> => {
    setErrorMessage(null);
    setLoading(true);
    try {
      const acceptedNegotiation: Negotiation = await NegotiationsUtils.acceptOffer(negotiation);
      setLoading(false);
      setNegotiation(acceptedNegotiation);
    } catch (e) {
      setLoading(false);
      setErrorMessage(e.error?.errors?.detail);
    }
  };

  const rejectOffer = async (): Promise<void> => {
    setErrorMessage(null);
    setLoading(true);
    try {
      const rejectedNegotiation: Negotiation = await NegotiationsUtils.rejectOffer(negotiation);
      setLoading(false);
      setNegotiation(rejectedNegotiation);
    } catch (e) {
      setLoading(false);
      setErrorMessage(e.error?.errors?.detail);
    }
  };

  return (
    <div
      className={clsx(
        styles.mmao__form,
        styles['offerStatus__bgColor--' + offerStatus.id],
        isSingleBtn && styles['mmao__form--singleBtn'],
      )}
    >
      <StatusMessage
        offerStatus={offerStatus}
        lastEventOfferDetails={lastEventOfferDetails}
      />

      <StatusAction
        nextActions={nextActions}
        isNextActionsShown={isNextActionsShown}
        setIsNextActionsShown={setIsNextActionsShown}
        negotiation={negotiation}
        negotiationProduct={negotiationProduct}
        isOrderButtonDisabled={isOrderButtonDisabled}
        acceptOffer={acceptOffer}
        rejectOffer={rejectOffer}
        selectAction={selectAction}
        addToCart={addToCart}
        isSingleBtn={isSingleBtn}
      />
    </div>
  );
};
